import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Send, CheckCircle, AlertCircle, Star } from '../../components/icons';
import { supabase } from '../../lib/supabase';

type FeedbackType = 'bug' | 'improvement' | 'feature_request' | 'general';
type FeatureArea = 'practice_conversation' | 'voice_practice' | 'feedback_analysis' | 'knowledge_resources' | 'setup_access' | 'general';

const FEEDBACK_TYPES: { id: FeedbackType; label: string; description: string }[] = [
  { id: 'bug', label: 'Bug Report', description: 'Something is not working correctly' },
  { id: 'improvement', label: 'Improvement', description: 'A suggestion to improve an existing feature' },
  { id: 'feature_request', label: 'Feature Request', description: 'A new feature you would like to see' },
  { id: 'general', label: 'General Feedback', description: 'Any other thoughts or comments' },
];

const FEATURE_AREAS: { id: FeatureArea; label: string }[] = [
  { id: 'practice_conversation', label: 'Practice Conversation' },
  { id: 'voice_practice', label: 'Voice Practice' },
  { id: 'feedback_analysis', label: 'Practice Feedback & Analysis' },
  { id: 'knowledge_resources', label: 'Knowledge Resources' },
  { id: 'setup_access', label: 'Setup & Access' },
  { id: 'general', label: 'General / Not Sure' },
];

export function NLPPracticeFeedback() {
  const navigate = useNavigate();
  const location = useLocation();
  const isPublic = location.pathname === '/feedback';

  const [feedbackType, setFeedbackType] = useState<FeedbackType | ''>('');
  const [featureArea, setFeatureArea] = useState<FeatureArea>('general');
  const [rating, setRating] = useState<number>(0);
  const [description, setDescription] = useState('');
  const [expectedBehavior, setExpectedBehavior] = useState('');
  const [actualBehavior, setActualBehavior] = useState('');
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleBack = () => navigate(isPublic ? '/' : '/coach/nlp-practice');

  const handleSubmit = async () => {
    if (!feedbackType || !description.trim()) {
      setError('Please select a feedback type and write a description.');
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      const { data: { user } } = await supabase.auth.getUser();

      const { error: insertError } = await supabase
        .from('nlp_practice_feedback')
        .insert({
          coach_id: user?.id || null,
          user_email: email.trim() || null,
          feedback_type: feedbackType,
          feature_area: featureArea,
          rating: rating > 0 ? rating : null,
          description: description.trim(),
          expected_behavior: feedbackType === 'bug' ? expectedBehavior.trim() || null : null,
          actual_behavior: feedbackType === 'bug' ? actualBehavior.trim() || null : null,
        });

      if (insertError) throw insertError;
      setSubmitted(true);
    } catch (err: any) {
      setError(err.message || 'Failed to submit feedback. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen freud-bg flex items-center justify-center px-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="max-w-md w-full"
        >
          <div className="freud-card-elevated p-8 text-center">
            <div className="w-16 h-16 bg-sage-100 rounded-full flex items-center justify-center mx-auto mb-5">
              <CheckCircle className="w-8 h-8 text-sage-600" />
            </div>
            <h1 className="freud-heading-xl text-chocolate-900 mb-3">Thank You!</h1>
            <p className="text-chocolate-500 mb-6">
              Your feedback has been submitted. We appreciate you taking the time to help us improve the NLP Practice experience.
            </p>
            <div className="flex gap-3 justify-center">
              <button
                onClick={handleBack}
                className="px-5 py-2.5 bg-sage-600 text-white rounded-xl text-sm font-semibold hover:bg-sage-700 transition-colors"
              >
                {isPublic ? 'Back to Home' : 'Back to Practice'}
              </button>
              <button
                onClick={() => {
                  setSubmitted(false);
                  setFeedbackType('');
                  setFeatureArea('general');
                  setRating(0);
                  setDescription('');
                  setExpectedBehavior('');
                  setActualBehavior('');
                  setEmail('');
                }}
                className="px-5 py-2.5 bg-cream-100 text-chocolate-600 rounded-xl text-sm font-semibold hover:bg-cream-200 transition-colors"
              >
                Submit Another
              </button>
            </div>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen freud-bg">
      <header className="bg-white/80 backdrop-blur-sm border-b border-cream-300 sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-4 md:px-8 py-4">
          <div className="flex items-center gap-3">
            <button
              onClick={handleBack}
              className="p-2 hover:bg-cream-200 rounded-xl transition-colors"
            >
              <ArrowLeft className="w-5 h-5 text-chocolate-600" />
            </button>
            <div>
              <h1 className="freud-heading-xl text-chocolate-900">Send Feedback</h1>
              <p className="text-sm text-chocolate-500">Help us improve NLP Skills Practice</p>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 md:px-8 py-8">
        {error && (
          <div className="mb-6 flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl">
            <AlertCircle className="w-5 h-5 flex-shrink-0" />
            <p className="text-sm">{error}</p>
          </div>
        )}

        <div className="space-y-6">
          <div>
            <label className="block text-sm font-semibold text-chocolate-800 mb-3">
              What type of feedback is this? <span className="text-red-500">*</span>
            </label>
            <div className="grid sm:grid-cols-2 gap-3">
              {FEEDBACK_TYPES.map((type) => (
                <button
                  key={type.id}
                  onClick={() => setFeedbackType(type.id)}
                  className={`freud-card-elevated p-4 text-left transition-all ${
                    feedbackType === type.id ? 'ring-2 ring-sage-500' : 'hover:ring-2 hover:ring-sage-300'
                  }`}
                >
                  <h3 className="text-sm font-bold text-chocolate-900 mb-1">{type.label}</h3>
                  <p className="text-xs text-chocolate-500">{type.description}</p>
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm font-semibold text-chocolate-800 mb-2">
              Which area of NLP Practice? <span className="text-red-500">*</span>
            </label>
            <select
              value={featureArea}
              onChange={(e) => setFeatureArea(e.target.value as FeatureArea)}
              className="w-full px-4 py-3 border border-cream-300 rounded-xl text-sm bg-white text-chocolate-700 focus:outline-none focus:ring-2 focus:ring-sage-400"
            >
              {FEATURE_AREAS.map((area) => (
                <option key={area.id} value={area.id}>{area.label}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-semibold text-chocolate-800 mb-2">
              How would you rate your experience? <span className="text-chocolate-400 font-normal">(optional)</span>
            </label>
            <div className="flex items-center gap-2">
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  key={star}
                  onClick={() => setRating(rating === star ? 0 : star)}
                  className={`p-1.5 rounded-lg transition-colors ${rating >= star ? 'text-amber-500' : 'text-cream-300 hover:text-cream-400'}`}
                >
                  <Star className="w-7 h-7" />
                </button>
              ))}
              {rating > 0 && (
                <span className="text-sm text-chocolate-500 ml-2">{rating} / 5</span>
              )}
            </div>
          </div>

          <div>
            <label className="block text-sm font-semibold text-chocolate-800 mb-2">
              Description <span className="text-red-500">*</span>
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={5}
              placeholder={
                feedbackType === 'bug'
                  ? 'Describe the issue in as much detail as possible...'
                  : feedbackType === 'feature_request'
                    ? 'Describe the feature you would like to see...'
                    : 'Share your thoughts...'
              }
              className="w-full px-4 py-3 border border-cream-300 rounded-xl text-sm bg-white text-chocolate-700 resize-none focus:outline-none focus:ring-2 focus:ring-sage-400"
            />
          </div>

          {feedbackType === 'bug' && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              className="space-y-4"
            >
              <div>
                <label className="block text-sm font-semibold text-chocolate-800 mb-2">
                  What did you expect to happen? <span className="text-chocolate-400 font-normal">(optional)</span>
                </label>
                <textarea
                  value={expectedBehavior}
                  onChange={(e) => setExpectedBehavior(e.target.value)}
                  rows={2}
                  placeholder="Describe what you expected..."
                  className="w-full px-4 py-3 border border-cream-300 rounded-xl text-sm bg-white text-chocolate-700 resize-none focus:outline-none focus:ring-2 focus:ring-sage-400"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-chocolate-800 mb-2">
                  What actually happened? <span className="text-chocolate-400 font-normal">(optional)</span>
                </label>
                <textarea
                  value={actualBehavior}
                  onChange={(e) => setActualBehavior(e.target.value)}
                  rows={2}
                  placeholder="Describe what actually happened..."
                  className="w-full px-4 py-3 border border-cream-300 rounded-xl text-sm bg-white text-chocolate-700 resize-none focus:outline-none focus:ring-2 focus:ring-sage-400"
                />
              </div>
            </motion.div>
          )}

          <div>
            <label className="block text-sm font-semibold text-chocolate-800 mb-2">
              Your email <span className="text-chocolate-400 font-normal">(optional — only if you want us to follow up)</span>
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              className="w-full px-4 py-3 border border-cream-300 rounded-xl text-sm bg-white text-chocolate-700 focus:outline-none focus:ring-2 focus:ring-sage-400"
            />
          </div>

          <div className="pt-2">
            <button
              onClick={handleSubmit}
              disabled={isSubmitting || !feedbackType || !description.trim()}
              className="w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 bg-sage-600 text-white rounded-xl font-semibold hover:bg-sage-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Send className="w-5 h-5" />
              {isSubmitting ? 'Submitting...' : 'Submit Feedback'}
            </button>
          </div>
        </div>
      </main>
    </div>
  );
}
